import { STOMP_ENDPOINT, getMe } from "@safers/api";
import { API_BASE_URL } from "./config";
import { Client } from "@stomp/stompjs";
import type { IMessage } from "@stomp/stompjs";

export type ConnectionStatus = "disconnected" | "connecting" | "connected" | "error";

type StatusListener = (status: ConnectionStatus) => void;

let client: Client | null = null;
let currentStatus: ConnectionStatus = "disconnected";
const listeners = new Set<StatusListener>();

function getWebSocketUrl(): string {
  const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${wsProtocol}//${window.location.host}${API_BASE_URL}${STOMP_ENDPOINT}`;
}

function setStatus(status: ConnectionStatus) {
  currentStatus = status;
  for (const listener of listeners) {
    try {
      listener(status);
    } catch (e) {
      void e;
    }
  }
}

export const stompService = {
  connect(onStatusChange?: StatusListener) {
    if (onStatusChange) {
      listeners.add(onStatusChange);
      onStatusChange(currentStatus);
    }
    if (client?.active) return;

    const instance = new Client({
      brokerURL: getWebSocketUrl(),
      reconnectDelay: 5000,
      debug: () => {},
      // 핸드셰이크만 액세스 쿠키를 본다. 쿠키가 만료돼 사라진 뒤 끊기면 재연결이 401 만
      // 반복하므로, 연결 전에 인증 요청을 하나 보내 401 이면 공통 클라이언트가 갱신하게 한다.
      // 여기서 던지면 stompjs 가 재연결을 멈추니 실패는 삼킨다.
      beforeConnect: async () => {
        await getMe().catch(() => {});
      },
      onConnect: () => {
        if (client === instance) setStatus("connected");
      },
      onDisconnect: () => {
        if (client === instance) setStatus("disconnected");
      },
      onStompError: () => {
        if (client === instance) setStatus("error");
      },
      onWebSocketClose: () => {
        if (client === instance) setStatus("disconnected");
      },
    });
    client = instance;

    setStatus("connecting");
    instance.activate();
  },

  subscribe(destination: string, handler: (message: IMessage) => void): () => void {
    if (!client?.connected) {
      return () => {};
    }

    const subscription = client.subscribe(destination, handler);
    return () => subscription.unsubscribe();
  },

  disconnect(onStatusChange?: StatusListener) {
    if (onStatusChange) listeners.delete(onStatusChange);
    if (listeners.size > 0) return;
    if (client?.active) {
      client.deactivate();
    }
    client = null;
    currentStatus = "disconnected";
  },

  get connected() {
    return client?.connected ?? false;
  },
};
